import { Box, Button, TextField, Typography } from "@material-ui/core";
import { graphql } from "gatsby";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import CheckmarkIcon from "../../components/icons/checkmark";
import CheckmarkCircleIcon from "../../components/icons/checkmark-circle";
import { formatMultilinesText } from "../../helpers/markdown";
import { useAuthContextSelector } from "../../hooks/use-auth";
import useCountry from "../../hooks/use-country";
import useSubcountry from "../../hooks/use-subcountry";
import useTranslate from "../../hooks/use-translate";
import { subscribeToNewsletter } from "../../services/newsletter";

export type FormDataType = {
  firstName: string;
  lastName: string;
  email: string;
};

type Props = Queries.DatoCmsCModuleMedicalFormSection;

export default function MedicalFormSection({
  confirmationText,
  containerBackgroundColor,
  legalTextNode,
  submitButtonLabel
}: Props) {
  const t = useTranslate();
  const user = useAuthContextSelector(c => c.user);
  const country = useCountry();
  const { subcountry } = useSubcountry();

  const [subscribed, setSubscribed] = useState(false);

  const { register, handleSubmit, errors, formState, setValue, setError } =
    useForm<FormDataType>();

  const handleFormSubmit = async (data: FormDataType) => {
    try {
      await subscribeToNewsletter(data.email, country, subcountry, "medical");
      setSubscribed(true);
    } catch (e) {
      setError("email", { type: "error", message: e?.message });
    }
  };

  const showConfirmation = !!subscribed;

  // After current user is loaded, prefill textfield with email
  useEffect(() => {
    if (user) {
      setValue("email", user.email);
    }
  }, [user]);

  return (
    <form
      onSubmit={handleSubmit(handleFormSubmit)}
      style={{ display: "block" }}
    >
      <Box
        display="flex"
        flexDirection="column"
        textAlign="center"
        maxWidth="400px"
        marginLeft="auto"
        marginRight="auto"
        position="relative"
        borderRadius="8px"
      >
        {/* First + Last name */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={2}
        >
          <Box flex={1} mr={1}>
            <TextField
              variant="outlined"
              id="firstName"
              name="firstName"
              placeholder={`${t("authentication.firstName")}`}
              fullWidth
              inputRef={register({
                required: t("error.required"),
                maxLength: "255"
              })}
              error={!!errors.firstName}
              helperText={errors.firstName?.message}
              disabled={formState.isSubmitting}
              autoComplete="given-name"
              InputLabelProps={{ shrink: false }}
            />
          </Box>
          <Box flex={1} ml={1}>
            <TextField
              variant="outlined"
              id="lastName"
              name="lastName"
              placeholder={`${t("authentication.lastName")}`}
              fullWidth
              inputRef={register({
                required: t("error.required"),
                maxLength: "255"
              })}
              error={!!errors.lastName}
              helperText={errors.lastName?.message}
              disabled={formState.isSubmitting}
              autoComplete="family-name"
              InputLabelProps={{ shrink: false }}
            />
          </Box>
        </Box>

        {/* Email field */}
        <TextField
          fullWidth
          variant="outlined"
          name="email"
          inputRef={register({
            required: t("error.required"),
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
              message: t("error.invalidEmail")
            }
          })}
          autoComplete="email"
          error={!!errors.email}
          placeholder={t("email")}
          disabled={formState.isSubmitting}
          helperText={errors.email?.message}
        />

        <Box mt={3}>
          <Button type="submit" variant="contained" className="white" fullWidth>
            {submitButtonLabel}
          </Button>
        </Box>

        {/* Legal text */}
        {!showConfirmation && (
          <Box mt={{ xs: 2, md: 2 }} maxWidth="380px" alignSelf="center">
            <Typography
              variant="caption"
              className="white white-links"
              style={{ lineHeight: "0.75" }}
              dangerouslySetInnerHTML={{
                __html: formatMultilinesText(
                  legalTextNode.childMarkdownRemark.html
                )
              }}
            />
          </Box>
        )}

        {/* Submit confirmation overlay */}
        {showConfirmation && (
          <Box
            position="absolute"
            top={0}
            left={0}
            width="100%"
            height="100%"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            textAlign="center"
            bgcolor={containerBackgroundColor}
            style={{ backgroundColor: "#055023" }}
            borderRadius="8px"
            paddingX={8}
          >
            <Typography variant="h5" className="white">
              {`${t("success")}!`}
            </Typography>
            <Box marginY={2}>
              <CheckmarkCircleIcon
                style={{ color: "white", fontSize: "4rem" }}
              />
            </Box>
            <Typography variant="body1" className="white">
              {confirmationText}
            </Typography>
          </Box>
        )}
      </Box>
    </form>
  );
}

export const MedicalFormSectionFragment = graphql`
  fragment DatoCmsCModuleMedicalFormSectionFragment on DatoCmsCModuleMedicalFormSection {
    legalTextNode {
      childMarkdownRemark {
        html
      }
    }
    confirmationText
    submitButtonLabel
    container
    innerContainer
    containerDisableGutters
    containerBackgroundColor
    innerContainer
    model {
      apiKey
    }
  }
`;
